import React, { useRef, useEffect, useCallback } from 'react';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import { useTranslation } from 'react-i18next';

const MainChatArea = ({
    activeProject,
    projects,
    projectsLoading,
    projectsError,
    handleProjectSelection,
    isSidebarOpen,
    onToggleSidebar,
    messages,
    isLoading,
    handleActionClick,
    dispatchWidgetAction,
    feedbackEnabled,
    handleRatingClick,
    feedbackState,
    handleSendMessage
}) => {

    const { t } = useTranslation();
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const isUserScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);
    const lastMessageCountRef = useRef(messages.length);

    const isTouchDevice = useCallback(() => {
        return (
            ('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0) ||
            (navigator.msMaxTouchPoints > 0)
        );
    }, []);

    const isMobileDevice = useCallback(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];

        return (
            isTouchDevice() &&
            (window.innerWidth <= 768 || mobileKeywords.some(keyword => userAgent.includes(keyword)))
        );
    }, [isTouchDevice]);

    const forceViewportResize = useCallback(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        if (!isSafari) return;

        window.scrollTo(0, 0);

        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 100);

        const originalHeight = document.body.style.height;
        document.body.style.height = '100vh';

        requestAnimationFrame(() => {
            document.body.style.height = originalHeight;
        });

        if (window.visualViewport) {
            const viewport = window.visualViewport;
            const viewportHeight = viewport.height;

            document.documentElement.style.setProperty(
                '--viewport-height',
                `${viewportHeight}px`
            );
        }
    }, []);

    const handleSendMessageWrapper = useCallback(async (messageText) => {
        // Solo marcar flag en móviles para prevenir blur durante auto-scroll
        if (isMobileDevice()) {
            isUserScrollingRef.current = false;

            setTimeout(() => {
                isUserScrollingRef.current = true;
            }, 1000);
        }

        await handleSendMessage(messageText);
    }, [handleSendMessage, isMobileDevice]);

    useEffect(() => {
        const isDesktop = !isMobileDevice();

        if (messages.length > lastMessageCountRef.current) {
            // Nuevo mensaje añadido
            if (isMobileDevice()) {
                isUserScrollingRef.current = false;
                setTimeout(() => {
                    isUserScrollingRef.current = true;
                }, 800);
            }
        }

        lastMessageCountRef.current = messages.length;
    }, [messages.length, isMobileDevice]);

    useEffect(() => {
        const isDesktop = !isMobileDevice();

        if (isDesktop && !isLoading && inputRef.current) {
            // Dar tiempo para que el DOM se actualice
            const timeoutId = setTimeout(() => {
                inputRef.current?.focus();
            }, 150);

            return () => clearTimeout(timeoutId);
        }
    }, [isLoading, isMobileDevice]);

    useEffect(() => {
        if (!window.visualViewport) return;

        const handleViewportResize = () => {
            const viewport = window.visualViewport;
            document.documentElement.style.setProperty(
                '--viewport-height',
                `${viewport.height}px`
            );
        };

        window.visualViewport.addEventListener('resize', handleViewportResize);
        window.visualViewport.addEventListener('scroll', handleViewportResize);

        handleViewportResize();

        return () => {
            window.visualViewport.removeEventListener('resize', handleViewportResize);
            window.visualViewport.removeEventListener('scroll', handleViewportResize);
        };
    }, []);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        if (!isMobileDevice()) {
            return; // ❌ No hacer nada en desktop
        }

        let isScrolling = false;
        let startY = 0;
        let startTime = 0;

        const handleTouchStart = (e) => {
            startY = e.touches[0].clientY;
            startTime = Date.now();
            isScrolling = false;
        };

        const handleTouchMove = (e) => {
            const currentY = e.touches[0].clientY;
            const diff = Math.abs(currentY - startY);
            const timeDiff = Date.now() - startTime;

            if (diff > 15 && timeDiff > 50) {
                isScrolling = true;
                isUserScrollingRef.current = true;
            }
        };

        const handleScroll = () => {
            if (!isUserScrollingRef.current || !isScrolling) return;

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                const activeElement = document.activeElement;
                const inputElement = inputRef.current?.getElement();

                if (inputElement && activeElement === inputElement) {
                    inputRef.current?.blur();

                    setTimeout(() => {
                        forceViewportResize();
                    }, 300);
                }
                isScrolling = false;
            }, 150);
        };

        const handleTouchEnd = () => {
            setTimeout(() => {
                isScrolling = false;
            }, 200);
        };

        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: true });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });
        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [forceViewportResize, isMobileDevice]);

    return (
        <div className="main-content">
            <div className="position-sticky sticky-top">

                <ChatHeader
                    activeProject={activeProject}
                    projects={projects}
                    projectsLoading={projectsLoading}
                    projectsError={projectsError}
                    handleProjectSelection={handleProjectSelection}
                    isSidebarOpen={isSidebarOpen}
                    onToggleSidebar={onToggleSidebar}
                />
            </div>

            <div className="chat-container" ref={messagesContainerRef}>
                <div
                    className="messages"
                    onClick={(e) => {
                        if (e.isDefaultPrevented() || e.isPropagationStopped()) return;
                        const target = e.target.closest('.chat-action-btn');
                        if (target) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleActionClick(target.dataset.action, { name: target.dataset.name });
                        }
                    }}
                >
                    <MessageList
                        messages={messages}
                        isLoading={isLoading}
                        onActionClick={handleActionClick}
                        onWidgetSubmit={dispatchWidgetAction}
                        feedbackEnabled={feedbackEnabled}
                        onRatingClick={handleRatingClick}
                        feedbackState={feedbackState}
                        projects={projects}
                        handleProjectSelection={handleProjectSelection}
                        handleSendMessage={handleSendMessage}

                    />
                </div>

            </div>
            <div className="position-sticky sticky-bottom">
                <ChatInput
                    ref={inputRef}
                    onSendMessage={handleSendMessageWrapper}
                    isLoading={isLoading}
                    placeholder={t('chat.input_placeholder',"Sign in to continue chatting...")}
                />
            </div>
        </div>
    );
};

export default MainChatArea;